import { createAsyncThunk } from '@reduxjs/toolkit';
import i18next from 'i18next';
import apiVRR from '../../libs/axios/apis';
import moment from 'moment';
import { RootState } from '../store';
import endPoints from '../../services/endpoints';
import { notify } from '../app/appSlice';
import { TBoostModalValues } from '../../components/Budget/Boost/BoostModal/types';
import { TDateRange } from '../../micro_front_end/nav_bar/date_time_picker/types';
import { TViewerList } from 'components/Share/ShareModal/ViewerLine/types';
import { TSelectionMetric } from '../../micro_front_end/overview/global_metrics/deck_metrics/card_metric/types';

export const fetchViewers = createAsyncThunk(
  'account/fetchViewers',
  async ({ brandId }: any) => {
    const response = await apiVRR('v2').get(
      endPoints.ACCOUNTS_ENDPOINTS.VIEWERS(brandId)
    );
    return response.data.list;
  }
);

export const revokeViewer = createAsyncThunk(
  'account/revokeViewer',
  async ({ email, brandId }: any, { dispatch }) => {
    const response = await apiVRR('v2').post(
      endPoints.ACCOUNTS_ENDPOINTS.VIEWERS(brandId),
      {
        email
      }
    );

    dispatch(fetchViewers({ brandId }));
    return response;
  }
);

export const fetchMetrics = createAsyncThunk(
  'overview/fetchMetrics',
  async (
    param: { brandId: string; period: TDateRange; p: string },
    { getState }
  ) => {
    return apiVRR('v2').get(endPoints.OVERVIEW.GLOBAL_METRICS(param.brandId), {
      params: {
        start: moment(param.period.from).format('YYYY-MM-DD'),
        end: moment(param.period.to).format('YYYY-MM-DD'),
        id: param.brandId,
        label: (getState() as any).app.funnel,
        provider: param.p
      }
    });
  }
);

export const updateMetrics = createAsyncThunk(
  'accounts/updateMetrics',
  async (newMetrics: TSelectionMetric[]) => {
    return {
      data: {
        data: newMetrics
      }
    };
  }
);

export const fetchCharts = createAsyncThunk(
  'overview/fetchCharts',
  async (
    param: {
      brandId: string;
      period: TDateRange;
      rangePeriod: string;
      p: string;
    },
    { getState }
  ) => {
    return apiVRR('v2').get(endPoints.OVERVIEW.CHARTS(param.brandId), {
      params: {
        id: param.brandId,
        start: moment(param.period.from).format('YYYY-MM-DD'),
        end: moment(param.period.to).format('YYYY-MM-DD'),
        period: param.rangePeriod || 'weekly',
        label: (getState() as any).app.funnel,
        provider: param.p
      }
    });
  }
);

export const fetchBudgets = createAsyncThunk(
  'overview/budgets',
  async (param: { brandId: string }) => {
    const endPoint = endPoints.OVERVIEW.BUDGETS(param.brandId);

    return apiVRR('v2').get(endPoint);
  }
);

export const optimizeCampaigns = createAsyncThunk(
  'account/optimize_campaigns',
  async (_param: never, { getState, dispatch }) => {
    const currentBrandId = (getState() as any).setting.vrr_preferences
      .current_brand_id;
    const response = await apiVRR('v2').post(
      endPoints.OVERVIEW.OPTIMIZATION(currentBrandId)
    );

    if (response.status >= 200 && response.status < 400) {
      const number_of_jobs = response.data.data.launched_jobs;
      const notif_type = number_of_jobs === 0 ? 'danger' : 'success';

      // Type success skips icon option
      dispatch(
        notify({
          type: notif_type,
          options: {
            type: notif_type,
            message: i18next.t(
              `components.optimize.notifications.number_of_job`,
              { count: number_of_jobs }
            ),
            icon: 'tim-icons icon-alert-circle-exc',
            place: 'tc'
          }
        })
      );
    }
    return response;
  }
);

export const fetchSplitTests = createAsyncThunk(
  'overview/fetchSplitTests',
  async (param: { brandId: string }) => {
    return apiVRR('v2').get(endPoints.OVERVIEW.SPLIT_TESTS(param.brandId));
  }
);

export const fetchSplitTestDetails = createAsyncThunk(
  'overview/SplitTestDetails',
  async (param: { brandId: string; splitTestId: string }) => {
    return apiVRR('v2').get(
      endPoints.OVERVIEW.SPLIT_TEST_DETAILS(param.brandId, param.splitTestId)
    );
  }
);

export const updateSplitTestBudgets = createAsyncThunk(
  'overview/updateSplitTestBudgets',
  async (
    param: {
      brandId: string;
      splitTestId: string;
      updatedBudgetValues: TBoostModalValues;
    },
    { dispatch }
  ) => {
    const response = await apiVRR('v2').put(
      endPoints.OVERVIEW.SPLIT_TEST_BUDGET_UPDATE(
        param.brandId,
        param.splitTestId
      ),
      {
        totalTop: param.updatedBudgetValues.totalTop,
        totalMiddle: param.updatedBudgetValues.totalMiddle,
        totalBottom: param.updatedBudgetValues.totalBottom,
        endDate: moment(param.updatedBudgetValues.endDate).format('YYYY-MM-DD')
      }
    );

    if (response.status >= 200 && response.status < 400) {
      dispatch(notify({ type: 'success' }));
    }

    return param.updatedBudgetValues;
  }
);

export const getSharerViewers = (state: RootState): TViewerList[] =>
  state.overview.viewers;

export const globalMetricsAll = (
  state: RootState
): TSelectionMetric[] | undefined => state.overview.globalMetrics;

export const getCharts = (state: RootState): any => state.overview.charts;

export const getBudgets = (state: RootState): any => state.overview.budgets;

export const getSplitTests = (state: RootState): any =>
  state.overview.split_tests;
